/**
 * Spec 79 M1.5 D0 — Orchestrator SSE event shapes.
 *
 * The conversation orchestrator streams its agentic-loop progress over the
 * existing /api/conversations/:id/events SSE channel. Frontend consumes these
 * to render the in-flight reasoning + tool-call ticker so users never see a
 * bare "thinking…" spinner.
 *
 * These events do NOT replace conversation_messages — they're transient
 * progress signals. The persistent record lives in role='tool' messages
 * written when each tool call completes.
 */
import { z } from 'zod';
import { ToolErrorSchema } from './tool-contracts.js';

// ─────────────────────────────────────────────────────────────────────────────
// Discriminated union of all orchestrator SSE event payloads
// ─────────────────────────────────────────────────────────────────────────────

/** Turn began. Includes the chosen system template name + estimated horizon. */
export const OrchestratorTurnStartedEventSchema = z.object({
  type: z.literal('orchestrator.turn_started'),
  conversationId: z.number().int().positive(),
  messageId: z.number().int().positive(),
  systemTemplate: z.string(),
  /** Soft estimate; UI uses this to show "planning N steps…" — never a hard cap. */
  estimatedSteps: z.number().int().min(1).max(20).optional(),
});

/** Sonnet started reasoning. Used to show a thinking indicator. */
export const OrchestratorThinkingStartedEventSchema = z.object({
  type: z.literal('orchestrator.thinking_started'),
  conversationId: z.number().int().positive(),
  messageId: z.number().int().positive(),
});

/** Sonnet emitted a redacted thinking summary (extended thinking mode). */
export const OrchestratorThinkingChunkEventSchema = z.object({
  type: z.literal('orchestrator.thinking_chunk'),
  conversationId: z.number().int().positive(),
  messageId: z.number().int().positive(),
  redactedSummary: z.string(),
});

/** Sonnet emitted a partial assistant text token. */
export const OrchestratorTextChunkEventSchema = z.object({
  type: z.literal('orchestrator.text_chunk'),
  conversationId: z.number().int().positive(),
  messageId: z.number().int().positive(),
  // Hard per-chunk cap so a runaway model can't grow a single SSE frame unbounded.
  // Client-side reducer also enforces a total streamingText cap.
  text: z.string().max(2000),
});

/**
 * The name of a tool AS REPORTED BY A TURN.
 *
 * These events describe whatever the agent actually called, which includes every entity-skill tool
 * (`load_skill`, `intel_source_list`, `dashboard_create`, `workflow_create`, …). Those are
 * registered at runtime from SKILL.md files, so they can never appear in a static list.
 *
 * Validating them against a closed list of nine names rejected every real turn: the client dropped each
 * `tool_call_started`/`completed` event as invalid, so the activity indicator stayed empty even
 * though the engine had published the full sequence.
 */
export const OrchestratorToolCallNameSchema = z.string().min(1).max(200);

/** A tool call has been dispatched (running). */
export const OrchestratorToolCallStartedEventSchema = z.object({
  type: z.literal('orchestrator.tool_call_started'),
  conversationId: z.number().int().positive(),
  messageId: z.number().int().positive(),
  toolUseId: z.string(),
  toolName: OrchestratorToolCallNameSchema,
  /** Truncated preview of the input (for UI; full input lives in tool message). */
  inputPreview: z.string().max(200),
});

/** Tool call succeeded — input + output already persisted as role='tool' message. */
export const OrchestratorToolCallCompletedEventSchema = z.object({
  type: z.literal('orchestrator.tool_call_completed'),
  conversationId: z.number().int().positive(),
  messageId: z.number().int().positive(),
  toolUseId: z.string(),
  toolName: OrchestratorToolCallNameSchema,
  /** ID of the conversation_messages row containing the full tool-call record. */
  toolMessageId: z.number().int().positive(),
  durationMs: z.number().int().min(0),
  /**
   * Optional `?panel=<type>:<id>` pointer when the tool produced a viewable artifact.
   * Capped to keep the SSE frame small and to bound the value the UI passes to
   * `parsePanelUrl`. Client validates the parsed `type` against the panel-type registry.
   */
  panelLink: z.string().max(200).nullable().optional(),
});

/** Tool call failed — included so UI can show retry/error UI. */
export const OrchestratorToolCallFailedEventSchema = z.object({
  type: z.literal('orchestrator.tool_call_failed'),
  conversationId: z.number().int().positive(),
  messageId: z.number().int().positive(),
  toolUseId: z.string(),
  toolName: OrchestratorToolCallNameSchema,
  error: ToolErrorSchema,
  willRetry: z.boolean(),
});

/** Final assistant message persisted; turn complete. */
export const OrchestratorTurnCompletedEventSchema = z.object({
  type: z.literal('orchestrator.turn_completed'),
  conversationId: z.number().int().positive(),
  messageId: z.number().int().positive(),
  /** ID of the final assistant conversation_messages row. */
  assistantMessageId: z.number().int().positive(),
  durationMs: z.number().int().min(0),
  toolCallCount: z.number().int().min(0),
});

/** Turn failed (budget exceeded, destructive partial failure, etc.). */
export const OrchestratorTurnFailedEventSchema = z.object({
  type: z.literal('orchestrator.turn_failed'),
  conversationId: z.number().int().positive(),
  messageId: z.number().int().positive(),
  reason: z.enum([
    'wall_budget_exceeded',
    'iteration_cap_exceeded',
    'tool_call_cap_exceeded',
    'destructive_partial',
    'cost_cap_exceeded',
    // The model stopped before finishing its answer — `max_tokens` is the reachable case, since
    // these turns carry a whole context pack plus the skill catalogue (QG-ENG-RT-005).
    'max_tokens',
    // The caller stopped the run (a workflow step's timeout, a cancelled run). What was written so far is kept.
    'cancelled',
    'unknown',
  ]),
  detail: z.string().max(500),
  durationMs: z.number().int().min(0),
});

// ─────────────────────────────────────────────────────────────────────────────
// Discriminated union — what the SSE handler sends out
// ─────────────────────────────────────────────────────────────────────────────

export const OrchestratorEventSchema = z.discriminatedUnion('type', [
  OrchestratorTurnStartedEventSchema,
  OrchestratorThinkingStartedEventSchema,
  OrchestratorThinkingChunkEventSchema,
  OrchestratorTextChunkEventSchema,
  OrchestratorToolCallStartedEventSchema,
  OrchestratorToolCallCompletedEventSchema,
  OrchestratorToolCallFailedEventSchema,
  OrchestratorTurnCompletedEventSchema,
  OrchestratorTurnFailedEventSchema,
]);

export type OrchestratorEvent = z.infer<typeof OrchestratorEventSchema>;

// Per-event type aliases (subscribers narrow with `event.type === '...'`).
export type OrchestratorTurnStartedEvent = z.infer<typeof OrchestratorTurnStartedEventSchema>;
export type OrchestratorThinkingStartedEvent = z.infer<typeof OrchestratorThinkingStartedEventSchema>;
export type OrchestratorThinkingChunkEvent = z.infer<typeof OrchestratorThinkingChunkEventSchema>;
export type OrchestratorTextChunkEvent = z.infer<typeof OrchestratorTextChunkEventSchema>;
export type OrchestratorToolCallStartedEvent = z.infer<typeof OrchestratorToolCallStartedEventSchema>;
export type OrchestratorToolCallCompletedEvent = z.infer<typeof OrchestratorToolCallCompletedEventSchema>;
export type OrchestratorToolCallFailedEvent = z.infer<typeof OrchestratorToolCallFailedEventSchema>;
export type OrchestratorTurnCompletedEvent = z.infer<typeof OrchestratorTurnCompletedEventSchema>;
export type OrchestratorTurnFailedEvent = z.infer<typeof OrchestratorTurnFailedEventSchema>;
